import React, { useEffect, useState } from 'react';
import { Trophy } from 'lucide-react';
import { dashboardAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { formatCurrency, ROLE_LABELS } from './format';

const Cell = ({ n, v, testId }) => (
    <td className="py-2 px-2 sm:px-3 text-right whitespace-nowrap" data-testid={testId}>
        <span className="font-semibold">{n}</span>
        {v !== undefined && <span className="block text-xs text-muted-foreground">{formatCurrency(v)}</span>}
    </td>
);

export const LeaderboardTable = () => {
    const [data, setData] = useState(null);
    useEffect(() => { dashboardAPI.getLeaderboard().then(r => setData(r.data)).catch(() => setData({ rows: [] })); }, []);
    if (!data) return null;
    const rows = data.rows || [];
    const tot = rows.reduce((a, r) => ({
        sc: a.sc + r.sold_count, sv: a.sv + r.sold_value, dc: a.dc + r.delivered_count, dv: a.dv + r.delivered_value, pc: a.pc + r.production_count,
    }), { sc: 0, sv: 0, dc: 0, dv: 0, pc: 0 });

    return (
        <Card data-testid="leaderboard-table">
            <CardHeader>
                <CardTitle className="font-heading uppercase text-lg flex items-center gap-2">
                    <Trophy size={18} className="text-amber-500" /> Classifica del mese {data.month}
                </CardTitle>
                <p className="text-sm text-muted-foreground">Veicoli e importi di questo mese per ogni venditore, ordinati per venduto.</p>
            </CardHeader>
            <CardContent className="overflow-x-auto px-2 sm:px-6">
                {rows.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Nessun dato per questo mese.</p>
                ) : (
                    <table className="w-full max-w-4xl text-sm">
                        <thead>
                            <tr className="border-b text-xs uppercase tracking-wider text-muted-foreground">
                                <th className="hidden sm:table-cell py-2 px-3 text-left">#</th>
                                <th className="py-2 px-2 sm:px-3 text-left">Venditore</th>
                                <th className="py-2 px-2 sm:px-3 text-right">Venduti</th>
                                <th className="py-2 px-2 sm:px-3 text-right">Consegn.</th>
                                <th className="py-2 px-2 sm:px-3 text-right"><span className="sm:hidden">In prod.</span><span className="hidden sm:inline">Entrati in produzione</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr key={r.user_id} className="border-b last:border-0 hover:bg-muted/40 transition-colors" data-testid={`leaderboard-row-${r.user_id}`}>
                                    <td className="hidden sm:table-cell py-2 px-3 font-heading font-bold text-muted-foreground">{i + 1}</td>
                                    <td className="py-2 px-2 sm:px-3">
                                        <span className="font-medium sm:whitespace-nowrap">{r.name}</span>
                                        <span className="hidden sm:inline ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">{ROLE_LABELS[r.role] || r.role}</span>
                                    </td>
                                    <Cell n={r.sold_count} v={r.sold_value} testId={`leaderboard-sold-${r.user_id}`} />
                                    <Cell n={r.delivered_count} v={r.delivered_value} testId={`leaderboard-delivered-${r.user_id}`} />
                                    <Cell n={r.production_count} testId={`leaderboard-production-${r.user_id}`} />
                                </tr>
                            ))}
                        </tbody>
                        {rows.length > 1 && (
                            <tfoot>
                                <tr className="border-t-2 font-semibold" data-testid="leaderboard-total">
                                    <td className="hidden sm:table-cell py-2 px-3" />
                                    <td className="py-2 px-2 sm:px-3 uppercase text-xs tracking-wider">Totale</td>
                                    <Cell n={tot.sc} v={tot.sv} />
                                    <Cell n={tot.dc} v={tot.dv} />
                                    <Cell n={tot.pc} />
                                </tr>
                            </tfoot>
                        )}
                    </table>
                )}
            </CardContent>
        </Card>
    );
};
